import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { hreflangLinks } from "@/lib/seo";
import { personSchema, breadcrumbSchema, PERSON_ID } from "@/lib/en-schema";
import { pageTitle } from "@/lib/brand";
import { getCop31Note, cop31NotePath } from "@/lib/cop31-notes";
import { cop31NoteBodies } from "@/lib/cop31-note-bodies";
import { getCop31NoteEnrichment } from "@/lib/cop31-note-health.functions";

const ShareButtons = lazy(() =>
  import("@/components/ShareButtons").then((m) => ({ default: m.ShareButtons })),
);


export const Route = createFileRoute("/cop31-guide_/notes/$slug")({
  loader: async ({ params }) => {
    const note = getCop31Note(params.slug);
    if (!note) throw notFound();
    if (params.slug !== note.enSlug) {
      throw redirect({ to: "/cop31-guide/notes/$slug", params: { slug: note.enSlug }, statusCode: 301 });
    }
    const ai = await getCop31NoteEnrichment({ data: { slug: note.slug } }).catch(() => null);
    return {
      note,
      body: cop31NoteBodies[note.slug]?.en ?? note.summaryEn,
      summary: ai?.summaryEn?.trim() || note.summaryEn,
      takeaways: ai?.takeawaysEn ?? [],
      concepts: ai?.concepts ?? [],
      sources: ai?.sources ?? [],
      reportLinks: ai?.reportLinks ?? [],
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [{ title: pageTitle("COP31 note not found", "en") }, { name: "robots", content: "noindex" }],
      };
    }
    const { note } = loaderData;
    const url = `https://smartsustainability.ai${cop31NotePath(note.slug, "en")}`;
    return {
      meta: [
        { title: pageTitle(`${note.titleEn} — COP31 note`, "en") },
        { name: "description", content: note.summaryEn },
        { property: "og:title", content: `${note.titleEn} — COP31 note` },
        { property: "og:description", content: note.summaryEn },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        // English-only notes have no Turkish page, so no alternate locale.
        ...(note.locale === "en"
          ? []
          : [{ property: "og:locale:alternate", content: "tr_TR" }]),
        { property: "article:published_time", content: `${note.iso}T00:00:00Z` },
        { property: "article:section", content: "Climate" },
        { property: "article:tag", content: "COP31" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...(note.locale === "en"
          ? [
              { rel: "alternate", hreflang: "en", href: url },
              { rel: "alternate", hreflang: "x-default", href: url },
            ]
          : hreflangLinks(cop31NotePath(note.slug, "tr"), cop31NotePath(note.slug, "en"))),
      ],
      scripts: [
        // Standalone author node so the byline resolves to one Person entity.
        { type: "application/ld+json", children: JSON.stringify(personSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AnalysisNewsArticle",
            headline: note.titleEn,
            description: note.summaryEn,
            datePublished: note.iso,
            inLanguage: "en",
            articleSection: "COP31 Analysis",
            mainEntityOfPage: url,
            dateModified: note.iso,
            author: { "@id": PERSON_ID },
            publisher: { "@id": PERSON_ID },
            isPartOf: {
              "@type": "WebPage",
              name: "COP31 Antalya 2026 — Guide and Analysis",
              url: "https://smartsustainability.ai/cop31-guide",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Home", item: "https://smartsustainability.ai/" },
              { name: "COP31 Guide", item: "https://smartsustainability.ai/cop31-guide" },
              { name: note.titleEn, item: url },
            ]),
          ),
        },
      ],
    };
  },
  component: NotePage,
});

function NotePage() {
  const { note, body, summary, takeaways, concepts, sources, reportLinks } = Route.useLoaderData();
  // Internal notes link to the Turkish essay, so they are not surfaced on the English page.
  const externalUrl = note.internal ? null : note.url;
  const sourceLinks = externalUrl
    ? [{ title: note.sourceLabel ?? "Original essay", url: externalUrl, host: new URL(externalUrl).host.replace(/^www\./, "") }, ...sources]
    : sources;
  return (
    <article className="mx-auto max-w-3xl px-6 py-16 md:py-24">
      <nav className="mb-8 text-[11px] uppercase tracking-[0.2em] text-clay">
        <Link to="/cop31-guide" className="underline underline-offset-4 hover:no-underline">
          COP31 Guide
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-muted-foreground">Analysis note</span>
      </nav>

      <p className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">
        {note.dateEn}
      </p>
      <h1 className="font-serif text-4xl leading-tight md:text-5xl text-balance">
        {note.titleEn}
      </h1>
      <p className="mt-6 font-serif text-lg leading-relaxed text-foreground/80 md:text-xl">
        {summary}
      </p>
      {takeaways.length > 0 && (
        <section className="mt-8 rounded border border-border bg-muted/30 p-5">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">
            Key takeaways
          </h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-foreground/85">
            {takeaways.map((item) => (
              <li key={item} className="flex gap-2">
                <span aria-hidden="true" className="text-clay">
                  —
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="mt-8 space-y-5 text-base leading-relaxed text-foreground/85">
        {body.split(/\n\n+/).map((paragraph) => (
          <p key={paragraph.slice(0, 80)}>{paragraph}</p>
        ))}
      </div>

      {concepts.length > 0 && (
        <section className="mt-8">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Related concepts
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {concepts.map((c) => (
              <Link
                key={c.id}
                to="/concepts/$slug"
                params={{ slug: c.slug }}
                className="rounded-full border border-border px-3 py-1 text-xs hover:border-clay hover:text-clay"
              >
                {c.label}
              </Link>
            ))}
          </div>
        </section>
      )}

      {(sourceLinks.length > 0 || reportLinks.length > 0) && (
        <section className="mt-10 border-t border-border pt-6">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Sources and related research
          </h2>
          <div className="mt-4 grid gap-5 md:grid-cols-2">
            {sourceLinks.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-clay">
                  Sources
                </h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                  {sourceLinks.map((source) => (
                    <li key={source.url}>
                      <a href={source.url} target="_blank" rel="noopener" className="underline underline-offset-4 hover:text-clay">
                        {source.title}
                      </a>
                      <span className="ml-2 text-xs text-muted-foreground">{source.host}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {reportLinks.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-clay">
                  Related research
                </h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                  {reportLinks.map((report) => (
                    <li key={report.url}>
                      <a href={report.url} target="_blank" rel="noopener" className="underline underline-offset-4 hover:text-clay">
                        {report.title}
                      </a>
                      <span className="ml-2 text-xs text-muted-foreground">{report.host}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      <Suspense fallback={null}>
        <ShareButtons
          path={cop31NotePath(note.slug, "en")}
          title={note.titleEn}
          excerpt={summary}
          category="COP31 note"
          origin="https://smartsustainability.ai"
          lang="en"
          size="md"
          className="mt-10 border-t border-border pt-6"
        />
      </Suspense>

      <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-border pt-6">

        {externalUrl && (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener"
            className="text-xs font-semibold uppercase tracking-[0.2em] text-clay underline underline-offset-4 hover:no-underline"
          >
            Read the original article ↗
          </a>
        )}
        <Link
          to="/cop31-guide"
          className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground underline underline-offset-4 hover:no-underline"
        >
          All COP31 notes
        </Link>
      </div>
    </article>
  );
}
